import React, {useEffect, useMemo, useState} from "react";
import {DateTime} from "luxon";
import {useLocation, useNavigate} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCalendarDays, faChevronLeft, faChevronRight, faLocationDot, faMapLocationDot, faStar} from "@fortawesome/free-solid-svg-icons";
import CalculationLoading from "../../components/calculation-loading";
import CalendarDayPanel from "../../components/calendar-day-panel";
import CalendarMonthGrid from "../../components/calendar-month-grid";
import CalendarYearHeatmap from "../../components/calendar-year-heatmap";
import {SegmentedFilter} from "../../components/segmented-filter";
import {buildCalendarMonthSummary, buildCalendarYearSummary, getCalendarAvailablePeriods, getCalendarHeatmapActivityScore} from "../../helpers/calendar/helpers";
import type {CalendarMonthSummary, CalendarYearSummary} from "../../helpers/calendar/types";
import {buildSpeciesDetailPaths} from "../../helpers/speciesDetailPaths";
import {pluralize} from "../../helpers/helpers";
import {CALENDAR_ROUTE} from "./constants";
import {getCalendarRoutePeriod} from "./helpers";
import type {CalendarViewProps} from "./types";
import "./calendar.scss";

const getValidYear = (value: string | undefined, fallback: number) => {
    const year = Number(value);
    return Number.isInteger(year) && year >= 1900 && year <= 2100 ? year : fallback;
};

const getValidMonth = (value: string | undefined, fallback: number) => {
    const month = Number(value);
    return Number.isInteger(month) && month >= 1 && month <= 12 ? month : fallback;
};

export default function Calendar({rawData, years, checklist}: CalendarViewProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const {year: routeYear, month: routeMonth} = getCalendarRoutePeriod(location.pathname);
    const availablePeriods = useMemo(() => getCalendarAvailablePeriods(rawData), [rawData]);
    const availableYears = useMemo(() => Array.from(new Set(availablePeriods.map(period => period.year))).sort((left, right) => right - left), [availablePeriods]);
    const fallbackYear = availableYears[0] ?? DateTime.local().year;
    const year = getValidYear(routeYear, fallbackYear);
    const month = getValidMonth(routeMonth, DateTime.local().month);
    const selectedDateFromUrl = new URLSearchParams(location.search).get('day') ?? undefined;
    const isYearView = new URLSearchParams(location.search).get('view') === 'year';
    const [summary, setSummary] = useState<CalendarMonthSummary>();
    const [yearSummary, setYearSummary] = useState<CalendarYearSummary>();
    const [isLoading, setIsLoading] = useState(true);
    const speciesDetailPaths = useMemo(() => buildSpeciesDetailPaths(checklist), [checklist]);

    useEffect(() => {
        let isActive = true;
        setIsLoading(true);
        const timer = window.setTimeout(() => {
            if (!isActive) return;
            if (isYearView) {
                setYearSummary(buildCalendarYearSummary(rawData, year));
            } else {
                setSummary(buildCalendarMonthSummary(rawData, year, month));
            }
            setIsLoading(false);
        }, 0);

        return () => {
            isActive = false;
            window.clearTimeout(timer);
        };
    }, [isYearView, month, rawData, year]);

    const selectedDate = useMemo(() => {
        if (selectedDateFromUrl && summary?.days[selectedDateFromUrl]) return selectedDateFromUrl;
        const activeDate = Object.keys(summary?.days ?? {}).find(date => DateTime.fromISO(date).month === month);
        return activeDate ?? DateTime.local(year, month, 1).toISODate();
    }, [month, selectedDateFromUrl, summary?.days, year]);
    const selectedDay = summary?.days[selectedDate];
    const monthLabel = DateTime.local(year, month).toFormat('LLLL yyyy');
    const selectedPeriodIndex = availablePeriods.findIndex(period => period.year === year && period.month === month);
    const previousPeriod = selectedPeriodIndex > 0 ? availablePeriods[selectedPeriodIndex - 1] : undefined;
    const nextPeriod = selectedPeriodIndex >= 0 ? availablePeriods[selectedPeriodIndex + 1] : undefined;

    const goToMonth = (nextYear: number, nextMonth: number, selectedDay?: string) => {
        const target = DateTime.local(nextYear, nextMonth, 1);
        const search = selectedDay ? `?day=${selectedDay}` : '';
        navigate(`${CALENDAR_ROUTE}/${target.year}/${target.toFormat('LL')}${search}`);
    };
    const changeMonth = (period?: {year: number, month: number}) => {
        if (period) goToMonth(period.year, period.month);
    };
    const selectDate = (date: string) => {
        const target = DateTime.fromISO(date);
        goToMonth(target.year, target.month, date);
    };
    const openMap = () => navigate(`/map?year=${year}&month=${month}`);
    const openDayMap = () => navigate(`/map?date=${selectedDate}`);
    const openMonthForDate = (date: string) => {
        const target = DateTime.fromISO(date);
        goToMonth(target.year, target.month, date);
    };
    const changeYear = (offset: number) => {
        const currentYearIndex = availableYears.indexOf(year);
        const targetYear = availableYears[currentYearIndex - offset];
        if (targetYear) navigate(`${CALENDAR_ROUTE}/${targetYear}?view=year`);
    };
    const calendarViewOptions = [
        {id: 'month' as const, label: 'Month', to: `${CALENDAR_ROUTE}/${year}/${DateTime.local(year, month).toFormat('LL')}`},
        {id: 'year' as const, label: 'Year', to: `${CALENDAR_ROUTE}/${year}?view=year`}
    ];

    return <section className="calendar-view" aria-labelledby="calendar-title">
        <header className="calendar-view__header">
            <div>
                <p className="calendar-view__eyebrow">Calendar Explorer</p>
                <h1 id="calendar-title">Your Birding Calendar</h1>
                <p>Revisit every year, month and day of your birding journey.</p>
            </div>
        </header>
        <div className="calendar-view__view-controls">
            <SegmentedFilter activeId={isYearView ? 'year' : 'month'} ariaLabel="Calendar view" options={calendarViewOptions} compact />
        </div>
        <div className="calendar-view__controls" aria-label="Calendar controls">
            <button type="button" aria-label={isYearView ? 'Previous year' : 'Previous month'} disabled={isYearView ? availableYears.indexOf(year) === availableYears.length - 1 : !previousPeriod} onClick={() => isYearView ? changeYear(-1) : changeMonth(previousPeriod)}><FontAwesomeIcon icon={faChevronLeft} aria-hidden="true" /></button>
            <label>
                <FontAwesomeIcon icon={faCalendarDays} aria-hidden="true" />
                <span className="visually-hidden">{isYearView ? 'Year' : 'Month'}</span>
                {isYearView ? <select value={year} onChange={event => navigate(`${CALENDAR_ROUTE}/${event.target.value}?view=year`)}>
                    {availableYears.map(calendarYear => <option key={calendarYear} value={calendarYear}>{calendarYear}</option>)}
                </select> : <select value={`${year}-${month}`} onChange={event => {
                    const [nextYear, nextMonth] = event.target.value.split('-').map(Number);
                    goToMonth(nextYear, nextMonth);
                }}>
                    {availablePeriods.slice().reverse().map(period => <option key={`${period.year}-${period.month}`} value={`${period.year}-${period.month}`}>
                        {DateTime.local(period.year, period.month).toFormat('LLLL yyyy')}
                    </option>)}
                </select>}
            </label>
            <button type="button" aria-label={isYearView ? 'Next year' : 'Next month'} disabled={isYearView ? availableYears.indexOf(year) === 0 : !nextPeriod} onClick={() => isYearView ? changeYear(1) : changeMonth(nextPeriod)}><FontAwesomeIcon icon={faChevronRight} aria-hidden="true" /></button>
            <button type="button" className="calendar-view__map-button" onClick={openMap}>
                <FontAwesomeIcon icon={faMapLocationDot} aria-hidden="true" /> View this month on the map
            </button>
        </div>
        {isLoading || (isYearView ? !yearSummary : !summary) ? <CalculationLoading title="Preparing your calendar" message="Summarizing your sightings by day..." /> : isYearView && yearSummary ? <>
            <div className="calendar-view__summary" aria-label={`${year} summary`}>
                <span><FontAwesomeIcon icon={faCalendarDays} aria-hidden="true" /><strong>{Object.keys(yearSummary.days).length}</strong> {pluralize(Object.keys(yearSummary.days).length, "birding day")}</span>
                <span><strong>{yearSummary.speciesCount}</strong> {pluralize(yearSummary.speciesCount, "species")}</span>
                <span><FontAwesomeIcon icon={faStar} aria-hidden="true" /><strong>{yearSummary.liferCount}</strong> {pluralize(yearSummary.liferCount, "lifer")}</span>
                <span><strong>{yearSummary.firstOfYearCount}</strong> {pluralize(yearSummary.firstOfYearCount, "year bird")}</span>
            </div>
            <CalendarYearHeatmap
                year={year}
                days={yearSummary.days}
                getActivityCount={getCalendarHeatmapActivityScore}
                getDayLabel={(date, day) => day
                    ? `${DateTime.fromISO(date).toFormat('cccc, LLLL d')}: ${day.speciesCount} ${pluralize(day.speciesCount, "species")}, ${day.checklistCount} ${pluralize(day.checklistCount, "checklist")}${day.liferCount ? `, ${day.liferCount} ${pluralize(day.liferCount, "lifer")}` : ''}${day.firstOfYearCount ? `, ${day.firstOfYearCount} ${pluralize(day.firstOfYearCount, "year bird")}` : ''}`
                    : `${DateTime.fromISO(date).toFormat('cccc, LLLL d')}: no birding activity`}
                onSelectDate={openMonthForDate}
                renderDayMarkers={day => <>
                    {day.liferCount ? <i className="calendar-year-heatmap__lifer" aria-hidden="true" /> : null}
                    {day.firstOfYearCount ? <i className="calendar-year-heatmap__year-bird" aria-hidden="true" /> : null}
                </>}
                legendMarkers={<>
                    <span className="calendar-year-heatmap__legend-marker calendar-year-heatmap__legend-marker--lifer">Lifer</span>
                    <span className="calendar-year-heatmap__legend-marker calendar-year-heatmap__legend-marker--year-bird">Year bird</span>
                </>}
            />
        </> : summary ? <>
            <div className="calendar-view__summary" aria-label={`${monthLabel} summary`}>
                <span><FontAwesomeIcon icon={faCalendarDays} aria-hidden="true" /><strong>{Object.values(summary.days).filter(day => DateTime.fromISO(day.date).month === month).length}</strong> {pluralize(Object.values(summary.days).filter(day => DateTime.fromISO(day.date).month === month).length, "birding day")}</span>
                <span><strong>{summary.speciesCount}</strong> {pluralize(summary.speciesCount, "species")}</span>
                <span><FontAwesomeIcon icon={faLocationDot} aria-hidden="true" /><strong>{summary.locationCount}</strong> {pluralize(summary.locationCount, "location")}</span>
                <span><FontAwesomeIcon icon={faStar} aria-hidden="true" /><strong>{summary.liferCount}</strong> {pluralize(summary.liferCount, "lifer")}</span>
            </div>
            <div className="calendar-view__content">
                <CalendarMonthGrid year={year} month={month} days={summary.days} biggestDayDate={summary.biggestDayDate} selectedDate={selectedDate} onSelectDate={selectDate} />
                <CalendarDayPanel
                    date={selectedDate}
                    day={selectedDay}
                    speciesDetailPaths={speciesDetailPaths}
                    onOpenDayMap={openDayMap}
                />
            </div>
        </> : null}
    </section>;
}
